import { defineStore } from 'pinia'
import { useStoreAuth } from '@/stores/storeAuth'
import { useStoreProfile } from '@/stores/storeProfile'
import { supabase } from '@/supabase'
import { watchEffect } from 'vue'
import { useStoreLists } from './storeLists'

export const useStoreTitlesUser = defineStore('storeTitleUser', {
  persist: false,
  state: () => ({
    loaded: false,
    items: [],
    initReady: false,
    currentUserId: null,
    recentTitles: [],
    // Batching system
    batchQueue: [],
    batchTimeout: null,
    batchSize: 15,
    batchTimeoutMs: 2000
  }),
  actions: {
    async init() {
      const storeAuth = useStoreAuth()
      if (!storeAuth.user) {
        console.log(
          'Skipping titles user initialization - user not authenticated'
        )
        return
      }

      this.currentUserId = storeAuth.user.id
      this.initReady = true
      this.subscribeToChanges(this.currentUserId)
    },

    subscribeToChanges(userId) {
      supabase
        .channel('public:titles_user')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'titles_user',
            filter: `profile_id=eq.${userId}`
          },
          (payload) => {
            console.log('INSERTED', payload)
            this.updateItems([payload.new])
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'titles_user',
            filter: `profile_id=eq.${userId}`
          },
          (payload) => {
            console.log('UPDATED', payload)
            this.updatePartialItem(payload.new)
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'titles_user',
            filter: `profile_id=eq.${userId}`
          },
          (payload) => {
            console.log('DELETED', payload)
            this.items = this.items.filter((item) => item.id !== payload.old.id)
          }
        )
        .subscribe()
    },

    updatePartialItem(updatedItem) {
      if (!updatedItem?.id) return

      const index = this.items.findIndex((item) => item.id === updatedItem.id)
      if (index !== -1) {
        const item = this.items[index]
        for (const [key, value] of Object.entries(updatedItem)) {
          if (item[key] !== value) {
            item[key] = value
          }
        }
      } else {
        this.fetchInfoById(updatedItem.id)
      }
    },

    updateItems(fetchedItems) {
      if (!fetchedItems?.length) return

      let hasNewItems = false
      const itemsById = {}

      this.items.forEach((item) => {
        if (item.id) {
          itemsById[item.id] = item
        }
      })

      const newItems = []
      fetchedItems.forEach((fetchedItem) => {
        if (!fetchedItem?.id) return

        const existingItem = itemsById[fetchedItem.id]
        if (existingItem) {
          for (const [key, value] of Object.entries(fetchedItem)) {
            if (existingItem[key] !== value) {
              existingItem[key] = value
            }
          }
        } else {
          hasNewItems = true
          newItems.push(fetchedItem)
        }
      })

      if (hasNewItems) {
        this.items = [...this.items, ...newItems]
      }
    },

    async fetchInfoById(id) {
      if (this.items.some((item) => item.id === id)) return

      await this._fetchItem({ key: 'id', value: id })
    },

    async fetchInfoByTId(title_id) {
      if (this.items.some((item) => item.title_id === title_id)) return

      await this._fetchItem({ key: 'title_id', value: title_id })
    },

    async refreshInfoByTId(title_id) {
      // Force refresh - skip the existence check
      await this._fetchItem({ key: 'title_id', value: title_id })
    },

    async fetchInfoByTitle(title, force = false) {
      if (!force && this.getTitleByTitleId(title)) return

      // Use batching for better performance unless force is requested
      if (!force) {
        this.queueTitleForBatch(title)
        return
      }

      await this._fetchItem({
        key: 'titles.tmdb_id',
        value: title.id,
        extraFilters: { 'titles.tmdb_type': title.type }
      })
    },

    async _fetchItem({ key, value, extraFilters = {} }) {
      if (!this.initReady) {
        watchEffect(async () => {
          if (this.initReady)
            await this._fetchItem({ key, value, extraFilters })
        })
        return
      }
      try {
        let query = supabase
          .from('titles_user')
          .select('*, titles(id, tmdb_id, tmdb_type)')
          .eq(key, value)
          .eq('profile_id', this.currentUserId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()

        for (const [filterKey, filterValue] of Object.entries(extraFilters)) {
          query = query.eq(filterKey, filterValue)
        }

        const { data, error } = await query

        if (error) {
          console.error('Error fetching user title info:', error)
          return
        }

        if (data) this.updateItems([data])
      } catch (dbError) {
        console.error('DB error', dbError)
      }
    },

    // Batching system for efficient bulk fetches
    queueTitleForBatch(titleQuery) {
      // Check if already in queue
      const alreadyQueued = this.batchQueue.some(
        (item) => item.id === titleQuery.id && item.type === titleQuery.type
      )

      if (alreadyQueued) return

      // Check if already loaded
      if (this.getTitleByTitleId(titleQuery)) return

      // Add to queue
      this.batchQueue.push(titleQuery)

      // Process if batch is full
      if (this.batchQueue.length >= this.batchSize) {
        this.processBatch()
        return
      }

      // Set timeout to process batch if not already set
      if (!this.batchTimeout) {
        this.batchTimeout = setTimeout(() => {
          this.processBatch()
        }, this.batchTimeoutMs)
      }
    },

    async processBatch() {
      if (this.batchQueue.length === 0) return

      // Clear timeout
      if (this.batchTimeout) {
        clearTimeout(this.batchTimeout)
        this.batchTimeout = null
      }

      // Take current batch and clear queue
      const batch = [...this.batchQueue]
      this.batchQueue = []

      try {
        await this._fetchBatch(batch)
      } catch (error) {
        console.error('Batch fetch error:', error)
      }
    },

    async _fetchBatch(titleQueries) {
      if (titleQueries.length === 0) return

      if (!this.initReady) {
        watchEffect(async () => {
          if (this.initReady) {
            await this._fetchBatch(titleQueries)
          }
        })
        return
      }
      try {
        // For batch queries, we need to use a different approach
        // Use the 'in' operator with arrays for better performance
        const tmdbIds = titleQueries.map((q) => q.id)
        const types = [...new Set(titleQueries.map((q) => q.type))] // unique types

        let query = supabase
          .from('titles_user')
          .select('*, titles!inner(id, tmdb_id, tmdb_type)')
          .eq('profile_id', this.currentUserId)
          .in('titles.tmdb_id', tmdbIds)
          .order('created_at', { ascending: false })

        // If we have multiple types, filter by them too
        if (types.length > 1) {
          query = query.in('titles.tmdb_type', types)
        } else if (types.length === 1) {
          query = query.eq('titles.tmdb_type', types[0])
        }

        const { data, error } = await query

        if (error) {
          console.error('Error batch fetching user title info:', error)
          return
        }

        if (data?.length) {
          this.updateItems(data)
        }
      } catch (dbError) {
        console.error('DB batch error', dbError)
      }
    },

    // Force process any pending batches immediately
    async forceBatchProcess() {
      if (this.batchQueue.length > 0) {
        await this.processBatch()
      }
    },

    // Cleanup method for clearing timeouts
    clearBatchTimeout() {
      if (this.batchTimeout) {
        clearTimeout(this.batchTimeout)
        this.batchTimeout = null
      }
    },

    // Log system is always enabled
    _isLogSystemEnabled() {
      return true
    },

    // Sync legacy state to log system (if enabled)
    async _syncToLogSystem(title, legacyState, newValue) {
      if (!this._isLogSystemEnabled()) return

      // Dynamically import to avoid circular dependency
      const { useStoreTitleLogs } = await import('./storeTitleLogs')
      const storeTitleLogs = useStoreTitleLogs()

      // Prevent infinite sync loops - don't sync if log system is currently syncing to us
      if (storeTitleLogs._isSyncing) return

      const titleData = {
        id: title.titleId,
        type: title.titleType
      }

      try {
        if (legacyState === 'seen') {
          if (newValue) {
            await storeTitleLogs.markAsSeen(titleData)
          } else {
            await storeTitleLogs.removeFromState(titleData, 'seen')
          }
        } else if (legacyState === 'watchlist') {
          if (newValue) {
            await storeTitleLogs.addToWatchlist(titleData)
          } else {
            await storeTitleLogs.removeFromState(titleData, 'watchlist')
          }
        }
        // faved has no log equivalent
      } catch (error) {
        console.error('[storeTitlesUser] Error syncing to log system:', error)
      }
    },

    async toggleSeen(title) {
      await this._toggleStatus('seen', title)
      useStoreLists().fetchSystemListByTypeAndUser(
        'seen',
        this.currentUserId,
        true
      )
    },

    async setSeen(newState, title) {
      console.log('SET SEEN - new state:', newState)
      await this._setStatus('seen', newState, title)

      // Sync to log system if enabled
      await this._syncToLogSystem(title, 'seen', newState)

      useStoreLists().fetchSystemListByTypeAndUser(
        'seen',
        this.currentUserId,
        true
      )
    },

    async toggleFaved(title) {
      await this._toggleStatus('faved', title)
      useStoreLists().fetchSystemListByTypeAndUser(
        'favourites',
        this.currentUserId,
        true
      )
    },

    async setFaved(newState, title) {
      console.log('SET FAVED - new state:', newState)
      await this._setStatus('faved', newState, title)
      await useStoreLists().fetchSystemListByTypeAndUser(
        'favourites',
        this.currentUserId,
        true
      )
    },

    async toggleWatchlist(title) {
      await this._toggleStatus('watchlist', title)

      useStoreLists().fetchSystemListByTypeAndUser(
        'watchlist',
        this.currentUserId,
        true
      )
    },

    async setWatchlist(newState, title) {
      await this._setStatus('watchlist', newState, title)

      // Sync to log system if enabled
      await this._syncToLogSystem(title, 'watchlist', newState)

      useStoreLists().fetchSystemListByTypeAndUser(
        'watchlist',
        this.currentUserId,
        true
      )
    },

    async _toggleStatus(action, title) {
      const { data, error } = await supabase.functions.invoke(
        `titles_user/${action}`,
        { body: { title } }
      )
      if (error) {
        console.error(`Error toggling ${action}`, error)
        return
      }
      console.log('Toggled', action, data)
      this.updatePartialItem(data.updated)
    },

    async _setStatus(action, newState, title, options = {}) {
      console.log('Set - ', title, newState, options)
      const { data, error } = await supabase.functions.invoke(
        `titles_user/${action}`,
        {
          body: {
            title,
            state: newState,
            skipFeedEvent: options.skipFeedEvent || false
          }
        }
      )
      if (error) {
        console.error(`Error toggling ${action}`, error)
        return
      }
      this.updatePartialItem(data.updated)
    },

    /**
     * Update standalone notes for a title
     * @param {Object} title - Title object with titleId and titleType
     * @param {string|null} notes - The note text (null to clear)
     */
    async updateNotes(title, notes) {
      if (!title?.titleId || !title?.titleType) {
        console.error('[storeTitlesUser] Invalid title for notes update')
        return { success: false, error: 'Invalid title' }
      }

      try {
        // First, ensure the title exists in our system
        const existingTitle = this.getTitleByTitleId({
          id: title.titleId,
          type: title.titleType
        })

        if (existingTitle) {
          // Update existing record
          const { data, error } = await supabase
            .from('titles_user')
            .update({ notes, updated_at: new Date().toISOString() })
            .eq('id', existingTitle.id)
            .select()
            .single()

          if (error) throw error

          // Update local state
          this.updatePartialItem(data)
          return { success: true, data }
        } else {
          // Need to create a titles_user record first
          // This requires the title to exist in the titles table
          const { data: titleData, error: titleError } = await supabase
            .from('titles')
            .select('id')
            .eq('tmdb_id', title.titleId)
            .eq('tmdb_type', title.titleType)
            .maybeSingle()

          if (titleError) throw titleError

          if (!titleData) {
            // Title doesn't exist, need to create it first via edge function
            const { error: createError } = await supabase.functions.invoke(
              'titles_user/notes',
              { body: { title, notes } }
            )
            if (createError) throw createError

            // Fetch the created record
            await this.fetchInfoByTitle(
              { id: title.titleId, type: title.titleType },
              true
            )
            return { success: true }
          }

          // Create titles_user record with notes
          const { data, error } = await supabase
            .from('titles_user')
            .insert({
              title_id: titleData.id,
              profile_id: this.currentUserId,
              notes
            })
            .select('*, titles(id, tmdb_id, tmdb_type)')
            .single()

          if (error) throw error

          this.updateItems([data])
          return { success: true, data }
        }
      } catch (error) {
        console.error('[storeTitlesUser] Error updating notes:', error)
        return { success: false, error: error.message }
      }
    },

    async addTitleToRecents(title) {
      // Add the titles to the recents array and push to the server
      // But, check if the title is already in the recents array
      // If it's not, add it to the array
      // If it is, update the timestamp, but only if it's not the most recent
      // If it is the most recent, do nothing
      // If the array is longer than 10, remove the oldest item
      // If we either added or updated, push this to the server as well

      let updateServer = false
      const recents = this.recentTitles
      const existingIndex = recents.findIndex(
        (item) =>
          item.titles?.tmdb_id === title.titleId &&
          item.titles?.tmdb_type === title.tmdbType
      )

      if (existingIndex !== -1) {
        if (existingIndex !== 0) {
          recents.splice(existingIndex, 1)
          recents.unshift({
            titles: {
              tmdb_id: title.titleId,
              tmdb_type: title.tmdbType
            }
          })
          updateServer = true
        }
      } else {
        recents.unshift({
          titles: {
            tmdb_id: title.titleId,
            tmdb_type: title.tmdbType
          }
        })
        updateServer = true
      }
      if (recents.length > 10) {
        recents.pop()
      }

      if (updateServer) {
        const { error } = await supabase.functions.invoke(
          `titles_user/recents`,
          { body: { title } }
        )
        if (error) {
          console.error(`Error adding to recents`, error)
          return
        }
      }
    }
  },
  getters: {
    getTitleByTId: (state) => (id) =>
      state.items.find((item) => item.title_id === id),
    getTitleByTitleId: (state) => (title) =>
      state.items.find(
        (item) =>
          // Use loose equality for tmdb_id since route params are strings
          item.titles?.tmdb_id == title.id &&
          item.titles?.tmdb_type === title.type
      )
  }
})
